import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { authAPI, plansAPI } from '../services/api';
import { Card, Button } from '../components/ui';
import Loading from '../components/common/Loading';
import { handleApiError, showToast } from '../utils/helpers';
import {
  Layers,
  Languages,
  User,
  Mail,
  Lock,
  Building2,
  FileText,
  Image as ImageIcon,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';

const STEPS = {
  PERSONAL: 1,
  AGENCY: 2,
  PLAN: 3,
  PAYMENT: 4,
};

const AgencySignupPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { isRTL, t, language, toggleLanguage } = useLanguage();
  const [currentStep, setCurrentStep] = useState(STEPS.PERSONAL);
  const [loading, setLoading] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [error, setError] = useState(null);
  const [plans, setPlans] = useState([]);

  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Personal Info
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    // Step 2: Agency Info
    agency_name: '',
    agency_description: '',
    agency_logo_url: '',
    // Step 3: Plan Selection
    plan_id: '',
    billing_period: 'month', // 'month' or 'year'
    // Step 4: Payment
    payment_method: '', // 'card' or 'bank_transfer'
  });

  // Fetch plans on component mount
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoadingPlans(true);
      const response = await plansAPI.getAll();
      setPlans(response.data.plans || []);
    } catch (err) {
      console.error('Failed to fetch plans:', err);
      setError(handleApiError(err));
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  const validateStep1 = () => {
    if (!formData.email || !formData.password || !formData.name) {
      setError(isRTL ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill in all required fields');
      return false;
    }

    if (formData.password.length < 12) {
      setError(isRTL ? 'كلمة المرور يجب أن تكون 12 حرفًا على الأقل' : 'Password must be at least 12 characters');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(isRTL ? 'كلمات المرور غير متطابقة' : 'Passwords do not match');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(isRTL ? 'يرجى إدخال بريد إلكتروني صحيح' : 'Please enter a valid email address');
      return false;
    }

    return true;
  };

  const validateStep2 = () => {
    if (!formData.agency_name) {
      setError(isRTL ? 'اسم الوكالة مطلوب' : 'Agency name is required');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.plan_id) {
      setError(isRTL ? 'يرجى اختيار باقة' : 'Please select a plan');
      return false;
    }
    return true;
  };

  const validateStep4 = () => {
    if (!formData.payment_method) {
      setError(isRTL ? 'يرجى اختيار طريقة الدفع' : 'Please select a payment method');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    setError(null);

    if (currentStep === STEPS.PERSONAL && !validateStep1()) return;
    if (currentStep === STEPS.AGENCY && !validateStep2()) return;
    if (currentStep === STEPS.PLAN && !validateStep3()) return;
    if (currentStep === STEPS.PAYMENT && !validateStep4()) return;

    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError(null);
  };

  const handleSubmit = async () => {
    if (!validateStep4()) return;

    setLoading(true);
    setError(null);

    try {
      // Create agency signup
      const signupData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        agency_name: formData.agency_name,
        agency_description: formData.agency_description || null,
        agency_logo_url: formData.agency_logo_url || null,
        plan_id: formData.plan_id,
        billing_period: formData.billing_period,
        payment_method: formData.payment_method,
      };

      const response = await authAPI.agencySignup(signupData);

      // Save token and user to localStorage
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      showToast.success(isRTL ? 'تم إنشاء حساب الوكالة بنجاح!' : 'Agency account created successfully!');

      // Check if payment is required based on system settings and plan
      const requiresPayment = response.data.requires_payment === true;

      // Redirect based on payment requirement
      if (requiresPayment) {
        // If trial is disabled and plan requires payment, redirect to payment page
        window.location.href = '/payment';
      } else {
        // If trial is enabled or plan is free, redirect to dashboard
        window.location.href = '/dashboard';
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.details || handleApiError(err);
      console.error('Signup error:', err.response?.data || err);
      setError(errorMessage);
      showToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = plans.find(p => p.id === formData.plan_id);
  
  // Calculate final price with discount
  const calculateFinalPrice = () => {
    if (!selectedPlan) return 0;
    
    const rawBasePrice = formData.billing_period === 'year' && selectedPlan.yearly_price
      ? selectedPlan.yearly_price
      : selectedPlan.monthly_price || selectedPlan.price || 0;
    
    const basePrice = parseFloat(rawBasePrice) || 0;
    
    const rawDiscount = formData.billing_period === 'year'
      ? (selectedPlan.yearly_discount || 0)
      : (selectedPlan.monthly_discount || 0);
    
    const discount = parseFloat(rawDiscount) || 0;
    
    if (discount > 0 && basePrice > 0) {
      return Math.round(basePrice * (1 - discount / 100) * 100) / 100;
    }
    return basePrice;
  };
  
  const planPrice = calculateFinalPrice();
  
  const rawPlanBasePrice = selectedPlan
    ? (formData.billing_period === 'year' && selectedPlan.yearly_price
        ? selectedPlan.yearly_price
        : selectedPlan.monthly_price || selectedPlan.price || 0)
    : 0;
  
  const planBasePrice = parseFloat(rawPlanBasePrice) || 0;
  
  const rawPlanDiscount = selectedPlan
    ? (formData.billing_period === 'year'
        ? (selectedPlan.yearly_discount || 0)
        : (selectedPlan.monthly_discount || 0))
    : 0;
  
  const planDiscount = parseFloat(rawPlanDiscount) || 0;

  if (loading && currentStep === STEPS.PAYMENT) {
    return <Loading fullScreen message={isRTL ? 'جاري إنشاء حسابك...' : 'Creating your account...'} />;
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-emerald-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Background Patterns */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 right-0 w-96 h-96 bg-sky-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-700"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-sky-100 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230ea5e9' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      ></div>

      <div className="max-w-4xl mx-auto relative z-10 animate-in fade-in duration-500">
        {/* Header with Logo & Language Toggle */}
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 group"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-sky-600 to-emerald-500 rounded-xl flex items-center justify-center text-white shadow-lg group-hover:shadow-sky-500/30 transition-all group-hover:scale-105">
              <Layers size={24} />
            </div>
            <span className="text-2xl font-bold text-slate-900 group-hover:text-sky-600 transition-colors">
              {isRTL ? 'سَلِس' : 'sls'}
              <span className="text-sky-600">.</span>
            </span>
          </Link>

          <button
            onClick={toggleLanguage}
            className="inline-flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-sky-600 border-2 border-slate-200 hover:border-sky-600 rounded-xl transition-all font-bold text-sm bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-md"
            title={isRTL ? 'Switch to English' : 'التبديل إلى العربية'}
          >
            <Languages className="w-4 h-4" />
            <span>{language === 'ar' ? 'EN' : 'عربي'}</span>
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">
              {isRTL ? 'الخطوة' : 'Step'} {currentStep} {isRTL ? 'من' : 'of'} 4
            </span>
            <span className="text-sm font-medium text-slate-700">
              {Math.round((currentStep / 4) * 100)}% {isRTL ? 'مكتمل' : 'Complete'}
            </span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-sky-600 to-emerald-500 h-2 rounded-full transition-all duration-500 shadow-lg"
              style={{ width: `${(currentStep / 4) * 100}%` }}
            />
          </div>
        </div>

        <Card className="p-8 shadow-2xl border-0 bg-white/90 backdrop-blur-sm">
          {/* Step 1: Personal Information */}
          {currentStep === STEPS.PERSONAL && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-2">
                  {isRTL ? 'معلوماتك الشخصية' : 'Your Personal Information'}
                </h2>
                <p className="text-slate-600">
                  {isRTL
                    ? 'ابدأ بإدخال بياناتك الأساسية لإنشاء حساب الوكالة'
                    : 'Start by filling in your personal details to create your agency account'}
                </p>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-bold text-slate-700">
                    {isRTL ? 'الاسم الكامل' : 'Full Name'} *
                  </label>
                  <div className="relative">
                    <div
                      className={`absolute inset-y-0 ${
                        isRTL ? 'right-0 pr-4' : 'left-0 pl-4'
                      } flex items-center pointer-events-none`}
                    >
                      <User className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full ${
                        isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'
                      } py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all bg-white text-slate-900 placeholder-slate-400`}
                      placeholder={isRTL ? 'أحمد محمد' : 'John Doe'}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-bold text-slate-700">
                    {isRTL ? 'البريد الإلكتروني' : 'Email Address'} *
                  </label>
                  <div className="relative">
                    <div
                      className={`absolute inset-y-0 ${
                        isRTL ? 'right-0 pr-4' : 'left-0 pl-4'
                      } flex items-center pointer-events-none`}
                    >
                      <Mail className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full ${
                        isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'
                      } py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all bg-white text-slate-900 placeholder-slate-400`}
                      placeholder="you@company.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-bold text-slate-700">
                    {isRTL ? 'كلمة المرور' : 'Password'} *
                  </label>
                  <div className="relative">
                    <div
                      className={`absolute inset-y-0 ${
                        isRTL ? 'right-0 pr-4' : 'left-0 pl-4'
                      } flex items-center pointer-events-none`}
                    >
                      <Lock className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`w-full ${
                        isRTL ? 'pl-12 pr-4' : 'pl-12 pr-4'
                      } py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all bg-white text-slate-900 placeholder-slate-400`}
                      placeholder={isRTL ? '12 حرفًا على الأقل' : 'At least 12 characters'}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-bold text-slate-700">
                    {isRTL ? 'تأكيد كلمة المرور' : 'Confirm Password'} *
                  </label>
                  <div className="relative">
                    <div
                      className={`absolute inset-y-0 ${
                        isRTL ? 'right-0 pr-4' : 'left-0 pl-4'
                      } flex items-center pointer-events-none`}
                    >
                      <Lock className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`w-full ${
                        isRTL ? 'pl-12 pr-4' : 'pl-12 pr-4'
                      } py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all bg-white text-slate-900 placeholder-slate-400`}
                      placeholder={isRTL ? 'أعد إدخال كلمة المرور' : 'Re-enter your password'}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Agency Information */}
          {currentStep === STEPS.AGENCY && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-2">
                  {isRTL ? 'معلومات الوكالة' : 'Agency Information'}
                </h2>
                <p className="text-slate-600">
                  {isRTL
                    ? 'أدخل تفاصيل وكالتك لتخصيص حسابك وتجربتك في النظام'
                    : 'Fill in your agency details to personalize your account and experience'}
                </p>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-bold text-slate-700">
                    {isRTL ? 'اسم الوكالة' : 'Agency Name'} *
                  </label>
                  <div className="relative">
                    <div
                      className={`absolute inset-y-0 ${
                        isRTL ? 'right-0 pr-4' : 'left-0 pl-4'
                      } flex items-center pointer-events-none`}
                    >
                      <Building2 className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="text"
                      name="agency_name"
                      value={formData.agency_name}
                      onChange={handleChange}
                      className={`w-full ${
                        isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'
                      } py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all bg-white text-slate-900 placeholder-slate-400`}
                      placeholder={isRTL ? 'اسم الوكالة' : 'Agency Name'}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-bold text-slate-700">
                    {isRTL ? 'وصف الوكالة (اختياري)' : 'Agency Description (Optional)'}
                  </label>
                  <div className="relative">
                    <div
                      className={`absolute ${
                        isRTL ? 'right-4 top-4' : 'left-4 top-4'
                      } pointer-events-none`}
                    >
                      <FileText className="h-5 w-5 text-slate-400" />
                    </div>
                    <textarea
                      name="agency_description"
                      value={formData.agency_description}
                      onChange={handleChange}
                      rows={4}
                      className={`w-full ${
                        isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'
                      } py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all bg-white text-slate-900 placeholder-slate-400 resize-none`}
                      placeholder={isRTL ? 'أخبرنا عن وكالتك...' : 'Tell us about your agency...'}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-bold text-slate-700">
                    {isRTL ? 'رابط الشعار (اختياري)' : 'Logo URL (Optional)'}
                  </label>
                  <div className="relative">
                    <div
                      className={`absolute inset-y-0 ${
                        isRTL ? 'right-0 pr-4' : 'left-0 pl-4'
                      } flex items-center pointer-events-none`}
                    >
                      <ImageIcon className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="url"
                      name="agency_logo_url"
                      value={formData.agency_logo_url}
                      onChange={handleChange}
                      className={`w-full ${
                        isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'
                      } py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all bg-white text-slate-900 placeholder-slate-400`}
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Plan Selection */}
          {currentStep === STEPS.PLAN && (
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                {isRTL ? 'اختر باقاتك' : 'Choose Your Plan'}
              </h2>
              
              {loadingPlans ? (
                <Loading />
              ) : (
                <>
                  {/* Billing Period Toggle */}
                  <div className="mb-6">
                    <div className="flex items-center justify-center gap-4">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, billing_period: 'month' }))}
                        className={`px-6 py-2 rounded-xl font-bold text-sm transition-all ${
                          formData.billing_period === 'month'
                            ? 'bg-sky-600 text-white shadow-lg shadow-sky-500/30'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        {isRTL ? 'شهري' : 'Monthly'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, billing_period: 'year' }))}
                        className={`px-6 py-2 rounded-xl font-bold text-sm transition-all relative ${
                          formData.billing_period === 'year'
                            ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-400/30'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        {isRTL ? 'سنوي' : 'Yearly'}
                        {plans.some(p => p.yearly_discount > 0) && (
                          <span className="ml-2 text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">
                            {isRTL ? 'خصم' : 'Save'}
                          </span>
                        )}
                      </button>
                    </div>
                    {formData.billing_period === 'year' && (
                      <p className="text-center text-sm text-slate-600 mt-2">
                        {isRTL 
                          ? '✨ احصل على خصم عند الاشتراك السنوي' 
                          : '✨ Get a discount with yearly subscription'}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {plans.map((plan) => {
                      // Ensure basePrice is a number
                      const rawBasePrice = formData.billing_period === 'year' && plan.yearly_price
                        ? plan.yearly_price
                        : plan.monthly_price || plan.price || 0;
                      
                      const basePrice = parseFloat(rawBasePrice) || 0;
                      
                      const discount = formData.billing_period === 'year' 
                        ? (parseFloat(plan.yearly_discount) || 0)
                        : (parseFloat(plan.monthly_discount) || 0);
                      
                      const finalPrice = discount > 0 && basePrice > 0
                        ? Math.round(basePrice * (1 - discount / 100) * 100) / 100
                        : basePrice;

                      const hasDiscount = discount > 0 && basePrice > 0;
                      
                      return (
                        <div
                          key={plan.id}
                          onClick={() => setFormData(prev => ({ ...prev, plan_id: plan.id }))}
                          className={`relative p-6 border-2 rounded-xl cursor-pointer transition-all hover:shadow-lg ${
                            formData.plan_id === plan.id
                              ? 'border-slate-900 bg-slate-50 ring-2 ring-slate-900 ring-offset-2'
                              : 'border-slate-200 hover:border-slate-400'
                          }`}
                        >
                          {/* Selected Badge */}
                          {formData.plan_id === plan.id && (
                            <div className={`absolute -top-3 ${isRTL ? 'left-4' : 'right-4'} bg-slate-900 text-white px-3 py-1 rounded-full text-xs font-semibold`}>
                              {isRTL ? 'محدد' : 'Selected'}
                            </div>
                          )}

                          {/* Discount Badge */}
                          {hasDiscount && (
                            <div className={`absolute -top-3 ${isRTL ? 'right-4' : 'left-4'} bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold`}>
                              {discount}% {isRTL ? 'خصم' : 'OFF'}
                            </div>
                          )}

                          <div className="text-center mb-4">
                            <h3 className="text-xl font-bold text-slate-900 mb-1">
                              {plan.name}
                            </h3>
                            {plan.name_ar && (
                              <p className="text-sm text-slate-600 mb-3">{plan.name_ar}</p>
                            )}
                            
                            {/* Pricing */}
                            <div className="mb-3">
                              {hasDiscount && (
                                <div className="text-sm text-slate-500 line-through mb-1">
                                  ${basePrice.toFixed(2)}
                                </div>
                              )}
                              <div className="text-3xl font-bold text-slate-900">
                                ${finalPrice.toFixed(2)}
                                <span className="text-sm text-slate-500 font-normal ml-1">
                                  /{formData.billing_period === 'year' ? (isRTL ? 'سنة' : 'year') : (isRTL ? 'شهر' : 'month')}
                                </span>
                              </div>
                            </div>
                            
                            {plan.description && (
                              <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                                {plan.description}
                              </p>
                            )}
                          </div>

                          {/* Limits */}
                          <div className={`space-y-2 text-sm mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                            {plan.max_posts !== null && plan.max_posts !== undefined ? (
                        <div className="flex items-center">
                          <CheckCircle className={`w-4 h-4 text-emerald-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          <span>{isRTL ? 'حتى' : 'Up to'} {plan.max_posts.toLocaleString()} {isRTL ? 'بوست' : 'posts'}</span>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <CheckCircle className={`w-4 h-4 text-emerald-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          <span>{isRTL ? 'بوستات غير محدودة' : 'Unlimited posts'}</span>
                              </div>
                            )}
                            {plan.max_users !== null && plan.max_users !== undefined ? (
                            <div className="flex items-center">
                              <CheckCircle className={`w-4 h-4 text-emerald-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                              <span>{isRTL ? 'حتى' : 'Up to'} {plan.max_users.toLocaleString()} {isRTL ? 'مستخدم' : 'users'}</span>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <CheckCircle className={`w-4 h-4 text-emerald-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                              <span>{isRTL ? 'مستخدمين غير محدودين' : 'Unlimited users'}</span>
                              </div>
                            )}
                            {plan.max_clients !== null && plan.max_clients !== undefined ? (
                            <div className="flex items-center">
                              <CheckCircle className={`w-4 h-4 text-emerald-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                              <span>{isRTL ? 'حتى' : 'Up to'} {plan.max_clients.toLocaleString()} {isRTL ? 'عميل' : 'clients'}</span>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <CheckCircle className={`w-4 h-4 text-emerald-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                              <span>{isRTL ? 'عملاء غير محدودين' : 'Unlimited clients'}</span>
                              </div>
                            )}
                          </div>

                          {/* Dynamic Features */}
                          {plan.features && Array.isArray(plan.features) && plan.features.length > 0 && (
                            <div className={`pt-4 border-t border-slate-200 space-y-2 text-sm ${isRTL ? 'text-right' : 'text-left'}`}>
                              {plan.features.slice(0, 4).map((feature) => (
                                <div key={feature.id} className="flex items-center">
                                  {feature.icon ? (
                                    <span className="mr-2">{feature.icon}</span>
                                  ) : (
                                    <span className="text-green-600 mr-2">✓</span>
                                  )}
                                  <span className="text-slate-700">
                                    {isRTL && feature.name_ar ? feature.name_ar : feature.name}
                                  </span>
                                </div>
                              ))}
                              {plan.features.length > 4 && (
                                <div className="text-xs text-slate-500 mt-2">
                                  +{plan.features.length - 4} {isRTL ? 'ميزات أخرى' : 'more features'}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Platforms (if any) */}
                          {plan.platforms && Array.isArray(plan.platforms) && plan.platforms.length > 0 && plan.platforms.length < 8 && (
                            <div className="pt-4 border-t border-slate-200 mt-4">
                              <div className="text-xs text-slate-600 mb-2">{isRTL ? 'المنصات المشمولة:' : 'Included Platforms:'}</div>
                              <div className="flex flex-wrap gap-1">
                                {plan.platforms.map((platform) => (
                                  <span
                                    key={platform}
                                    className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded"
                                  >
                                    {platform}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Plan Comparison Section */}
                  {plans.length > 1 && (
                    <div className="mt-12 pt-8 border-t border-slate-200">
                      <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">
                        {isRTL ? 'مقارنة الباقات' : 'Compare Plans'}
                      </h3>
                      
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-slate-50">
                              <th className={`p-4 text-left font-semibold text-slate-900 border-b border-slate-200 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {isRTL ? 'الميزة' : 'Feature'}
                              </th>
                              {plans.map((plan) => {
                                const basePrice = formData.billing_period === 'year' && plan.yearly_price
                                  ? parseFloat(plan.yearly_price) || 0
                                  : parseFloat(plan.monthly_price) || parseFloat(plan.price) || 0;
                                
                                const discount = formData.billing_period === 'year'
                                  ? (parseFloat(plan.yearly_discount) || 0)
                                  : (parseFloat(plan.monthly_discount) || 0);
                                
                                const finalPrice = discount > 0 && basePrice > 0
                                  ? Math.round(basePrice * (1 - discount / 100) * 100) / 100
                                  : basePrice;

                                return (
                                  <th
                                    key={plan.id}
                                    className={`p-4 text-center font-semibold text-slate-900 border-b border-slate-200 ${
                                      formData.plan_id === plan.id ? 'bg-slate-100 ring-2 ring-slate-900' : ''
                                    }`}
                                  >
                                    <div className="font-bold text-lg mb-1">{plan.name}</div>
                                    {plan.name_ar && (
                                      <div className="text-sm text-slate-600 mb-2">{plan.name_ar}</div>
                                    )}
                                    <div className="text-2xl font-bold text-slate-900">
                                      ${finalPrice.toFixed(2)}
                                      <span className="text-xs text-slate-500 font-normal">
                                        /{formData.billing_period === 'year' ? (isRTL ? 'سنة' : 'year') : (isRTL ? 'شهر' : 'month')}
                                      </span>
                                    </div>
                                    {discount > 0 && (
                                      <div className="text-xs text-red-600 mt-1">
                                        {discount}% {isRTL ? 'خصم' : 'OFF'}
                                      </div>
                                    )}
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {/* Pricing Row */}
                            <tr className="border-b border-slate-100">
                              <td className={`p-4 font-medium text-slate-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {isRTL ? 'السعر' : 'Price'}
                              </td>
                              {plans.map((plan) => {
                                const basePrice = formData.billing_period === 'year' && plan.yearly_price
                                  ? parseFloat(plan.yearly_price) || 0
                                  : parseFloat(plan.monthly_price) || parseFloat(plan.price) || 0;
                                
                                const discount = formData.billing_period === 'year'
                                  ? (parseFloat(plan.yearly_discount) || 0)
                                  : (parseFloat(plan.monthly_discount) || 0);
                                
                                const finalPrice = discount > 0 && basePrice > 0
                                  ? Math.round(basePrice * (1 - discount / 100) * 100) / 100
                                  : basePrice;

                                return (
                                  <td
                                    key={plan.id}
                                    className={`p-4 text-center ${
                                      formData.plan_id === plan.id ? 'bg-slate-50' : ''
                                    }`}
                                  >
                                    <div className="font-semibold text-slate-900">
                                      ${finalPrice.toFixed(2)}
                                    </div>
                                    {discount > 0 && (
                                      <div className="text-xs text-slate-500 line-through">
                                        ${basePrice.toFixed(2)}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>

                            {/* Max Posts */}
                            <tr className="border-b border-slate-100">
                              <td className={`p-4 font-medium text-slate-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {isRTL ? 'عدد البوستات' : 'Posts'}
                              </td>
                              {plans.map((plan) => (
                                <td
                                  key={plan.id}
                                  className={`p-4 text-center ${
                                    formData.plan_id === plan.id ? 'bg-slate-50' : ''
                                  }`}
                                >
                                  {plan.max_posts !== null && plan.max_posts !== undefined ? (
                                    <span className="text-slate-900">{plan.max_posts.toLocaleString()}</span>
                                  ) : (
                                    <span className="text-green-600 font-semibold">
                                      {isRTL ? 'غير محدود' : 'Unlimited'}
                                    </span>
                                  )}
                                </td>
                              ))}
                            </tr>

                            {/* Max Users */}
                            <tr className="border-b border-slate-100">
                              <td className={`p-4 font-medium text-slate-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {isRTL ? 'عدد المستخدمين' : 'Users'}
                              </td>
                              {plans.map((plan) => (
                                <td
                                  key={plan.id}
                                  className={`p-4 text-center ${
                                    formData.plan_id === plan.id ? 'bg-slate-50' : ''
                                  }`}
                                >
                                  {plan.max_users !== null && plan.max_users !== undefined ? (
                                    <span className="text-slate-900">{plan.max_users.toLocaleString()}</span>
                                  ) : (
                                    <span className="text-green-600 font-semibold">
                                      {isRTL ? 'غير محدود' : 'Unlimited'}
                                    </span>
                                  )}
                                </td>
                              ))}
                            </tr>

                            {/* Max Clients */}
                            <tr className="border-b border-slate-100">
                              <td className={`p-4 font-medium text-slate-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {isRTL ? 'عدد العملاء' : 'Clients'}
                              </td>
                              {plans.map((plan) => (
                                <td
                                  key={plan.id}
                                  className={`p-4 text-center ${
                                    formData.plan_id === plan.id ? 'bg-slate-50' : ''
                                  }`}
                                >
                                  {plan.max_clients !== null && plan.max_clients !== undefined ? (
                                    <span className="text-slate-900">{plan.max_clients.toLocaleString()}</span>
                                  ) : (
                                    <span className="text-green-600 font-semibold">
                                      {isRTL ? 'غير محدود' : 'Unlimited'}
                                    </span>
                                  )}
                                </td>
                              ))}
                            </tr>

                            {/* Platforms */}
                            <tr className="border-b border-slate-100">
                              <td className={`p-4 font-medium text-slate-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                {isRTL ? 'المنصات' : 'Platforms'}
                              </td>
                              {plans.map((plan) => (
                                <td
                                  key={plan.id}
                                  className={`p-4 text-center ${
                                    formData.plan_id === plan.id ? 'bg-slate-50' : ''
                                  }`}
                                >
                                  {plan.platforms && Array.isArray(plan.platforms) && plan.platforms.length > 0 ? (
                                    <span className="text-sm text-slate-700">
                                      {plan.platforms.length === 1 
                                        ? plan.platforms[0] 
                                        : `${plan.platforms.length} ${isRTL ? 'منصات' : 'platforms'}`}
                                    </span>
                                  ) : (
                                    <span className="text-green-600 font-semibold">
                                      {isRTL ? 'جميع المنصات' : 'All Platforms'}
                                    </span>
                                  )}
                                </td>
                              ))}
                            </tr>

                            {/* Dynamic Features */}
                            {(() => {
                              // Get all unique features from all plans
                              const allFeatures = new Map();
                              plans.forEach(plan => {
                                if (plan.features && Array.isArray(plan.features)) {
                                  plan.features.forEach(feature => {
                                    if (!allFeatures.has(feature.id)) {
                                      allFeatures.set(feature.id, feature);
                                    }
                                  });
                                }
                              });

                              if (allFeatures.size === 0) return null;

                              return Array.from(allFeatures.values())
                                .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                                .map((feature) => (
                                  <tr key={feature.id} className="border-b border-slate-100">
                                    <td className={`p-4 font-medium text-slate-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                                      <div className="flex items-center">
                                        {feature.icon && <span className="mr-2">{feature.icon}</span>}
                                        <span>{isRTL && feature.name_ar ? feature.name_ar : feature.name}</span>
                                      </div>
                                    </td>
                                    {plans.map((plan) => {
                                      const hasFeature = plan.features && Array.isArray(plan.features)
                                        ? plan.features.some(f => f.id === feature.id)
                                        : false;

                                      return (
                                        <td
                                          key={plan.id}
                                          className={`p-4 text-center ${
                                            formData.plan_id === plan.id ? 'bg-slate-50' : ''
                                          }`}
                                        >
                                          {hasFeature ? (
                                            <span className="text-green-600 text-xl">✓</span>
                                          ) : (
                                            <span className="text-slate-300 text-xl">✗</span>
                                          )}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ));
                            })()}
                          </tbody>
                        </table>
                      </div>

                      <div className="mt-6 text-center">
                        <p className="text-sm text-slate-600">
                          {isRTL 
                            ? '💡 اختر الباقة التي تناسب احتياجاتك' 
                            : '💡 Choose the plan that fits your needs'}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 4: Payment */}
          {currentStep === STEPS.PAYMENT && (
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                {isRTL ? 'طريقة الدفع' : 'Payment Method'}
              </h2>
              
              {selectedPlan && (
                <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-900">
                        {selectedPlan.name}
                        {selectedPlan.name_ar && (
                          <span className="text-slate-600 font-normal"> ({selectedPlan.name_ar})</span>
                        )}
                      </p>
                      <p className="text-sm text-slate-600">
                        {formData.billing_period === 'year' ? (isRTL ? 'سنوي' : 'Yearly') : (isRTL ? 'شهري' : 'Monthly')}
                      </p>
                    </div>
                    <div className={`text-right ${isRTL ? 'text-left' : ''}`}>
                      {planDiscount > 0 && (
                        <div className="text-sm text-slate-500 line-through mb-1">
                          ${planBasePrice.toFixed(2)}
                        </div>
                      )}
                      <div className="text-xl font-bold text-slate-900">
                        ${planPrice.toFixed(2)}
                        <span className="text-sm text-slate-500 font-normal ml-1">
                          /{formData.billing_period === 'year' ? (isRTL ? 'سنة' : 'year') : (isRTL ? 'شهر' : 'month')}
                        </span>
                      </div>
                      {planDiscount > 0 && (
                        <div className="text-xs text-red-600 font-medium mt-1">
                          {planDiscount}% {isRTL ? 'خصم' : 'discount'} {isRTL ? 'مطبق' : 'applied'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div
                  onClick={() => setFormData(prev => ({ ...prev, payment_method: 'card' }))}
                  className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.payment_method === 'card'
                      ? 'border-slate-900 bg-slate-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center text-white font-bold">
                      💳
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        {isRTL ? 'الدفع بالبطاقة' : 'Credit/Debit Card'}
                      </h3>
                      <p className="text-sm text-slate-600">
                        {isRTL ? 'سيتم توجيهك إلى بوابة الدفع الآمنة' : 'You will be redirected to a secure payment gateway'}
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  onClick={() => setFormData(prev => ({ ...prev, payment_method: 'bank_transfer' }))}
                  className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.payment_method === 'bank_transfer'
                      ? 'border-slate-900 bg-slate-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center text-white font-bold">
                      🏦
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        {isRTL ? 'تحويل بنكي' : 'Bank Transfer'}
                      </h3>
                      <p className="text-sm text-slate-600">
                        {isRTL ? 'سيتم إنشاء طلب دفع وستتلقى تفاصيل التحويل البنكي' : 'A payment request will be created and you will receive bank transfer details'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  {isRTL 
                    ? 'ملاحظة: ستحصل على فترة تجربة مجانية لمدة 14 يوم بعد التسجيل. يمكنك الاشتراك لاحقاً.'
                    : 'Note: You will get a 14-day free trial period after registration. You can subscribe later.'}
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mt-6 rounded-xl bg-red-50 border-2 border-red-200 p-4 animate-in slide-in-from-top duration-300">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="mt-8 flex justify-between items-center">
            <div>
              {currentStep > 1 && (
                <Button
                  onClick={handleBack}
                  variant="secondary"
                  className="border-2"
                >
                  {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                  {isRTL ? 'السابق' : 'Back'}
                </Button>
              )}
            </div>
            <div>
              {currentStep < 4 ? (
                <Button
                  onClick={handleNext}
                  variant="primary"
                  className="bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-lg shadow-sky-500/30"
                >
                  {isRTL ? 'التالي' : 'Next'}
                  {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  variant="primary"
                  className="bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-lg shadow-sky-500/30"
                >
                  {loading 
                    ? (isRTL ? 'جاري الإنشاء...' : 'Creating Account...') 
                    : (isRTL ? 'إنشاء الحساب' : 'Create Account')}
                </Button>
              )}
            </div>
          </div>

          {/* Login Link */}
          <div className="mt-6 text-center text-sm text-slate-600">
            {isRTL ? 'لديك حساب بالفعل؟' : 'Already have an account?'}{' '}
            <button
              onClick={() => navigate('/login')}
              className="font-medium text-slate-900 hover:text-slate-700"
            >
              {isRTL ? 'تسجيل الدخول' : 'Sign in'}
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AgencySignupPage;
